import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import Select from 'react-select'
import { ZodIssue } from 'zod'
import { ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import { hasText } from 'shared/lib/common-util'
import useCommodityCodeSearch, { CombinedNomenclatureOption } from './useCommodityCodeSearch'

import { CommodityCodeOption, DIGIT_OR_WHITESPACE, mapToOptions } from './commodityCodeSearchOptions'

export interface CommodityCodeSearchProps {
  country: string | null
  isEns: boolean
}

function CommodityCodeSearch({ country, isEns }: CommodityCodeSearchProps) {
  const { t } = useTranslation()
  const {
    setValue, setError, control,
  } = useFormContext<ConsignmentItemFormValues>()

  const [wasPreviousPageLast, setWasPreviousPageLast] = useState(false)
  const [currentSearch, setCurrentSearch] = useState<string>('')
  const [pageNumber, setPageNumber] = useState(0)
  const [options, setOptions] = useState<CombinedNomenclatureOption[]>([])
  const [selectedOption, setSelectedOption] = useState<CombinedNomenclatureOption>()
  const [isInvalidCode, setIsInvalidCode] = useState(false)

  const {
    isLoading,
    data,
  } = useCommodityCodeSearch(currentSearch, pageNumber, country)

  const [harmonizedSystemSubHeadingCode, combinedNomenclatureCode] = useWatch({
    control,
    name: ['commodityHarmonizedSystemSubHeadingCode', 'commodityCombinedNomenclatureCode'],
  })

  useEffect(() => {
    if (!hasText(harmonizedSystemSubHeadingCode) && !hasText(combinedNomenclatureCode)) {
      return
    }

    const hsCode = harmonizedSystemSubHeadingCode.trim()
    const cnCode = combinedNomenclatureCode.trim()
    const hsCodeLabel = hsCode.length > 4 ? `${hsCode.substring(0, 4)} ${hsCode.substring(4, 6)}` : hsCode
    const prefilledInput = `${hsCodeLabel} ${cnCode}`.trim()
    setCurrentSearch(prefilledInput)

    setSelectedOption({
      value: hsCode + cnCode,
      codeLabel: prefilledInput,
      name: '',
      isArchived: false,
    })
  }, [harmonizedSystemSubHeadingCode, combinedNomenclatureCode])

  useEffect(() => {
    if (!data) return

    const newOptions = mapToOptions(data.content, country, isEns)

    setOptions((prevOptions) => (pageNumber !== 0
      ? [...prevOptions, ...newOptions]
      : newOptions))

    if (pageNumber === 0) {
      const optionsMatch = newOptions.find((option) => option.value === selectedOption?.value)
      if (optionsMatch) {
        setSelectedOption({
          value: optionsMatch?.value ?? '',
          codeLabel: optionsMatch?.codeLabel ?? '',
          name: optionsMatch?.name ?? '',
          isArchived: optionsMatch?.isArchived ?? false,
        })
      }
    }

    setWasPreviousPageLast(data.last)
  }, [country, data, isEns, pageNumber, selectedOption?.value])

  useEffect(() => {
    setPageNumber(0)
    setWasPreviousPageLast(false)
  }, [currentSearch])

  useEffect(() => {
    if (!selectedOption) return

    const commodityCode = selectedOption.value ?? ''
    const opts = { shouldDirty: true }
    if (commodityCode.length > 6) {
      setValue('commodityHarmonizedSystemSubHeadingCode', commodityCode.substring(0, 6), opts)
      setValue('commodityCombinedNomenclatureCode', commodityCode.substring(6, commodityCode.length), opts)
      setValue('commodityCodeArchived', selectedOption?.isArchived, opts)
    } else {
      setValue('commodityHarmonizedSystemSubHeadingCode', commodityCode, opts)
      setValue('commodityCombinedNomenclatureCode', '', opts)
      setValue('commodityCodeArchived', selectedOption?.isArchived, opts)
    }

    if (options.length === 0 && hasText(commodityCode)) {
      setIsInvalidCode(true)
      setError('commodityHarmonizedSystemSubHeadingCode', {
        code: 'custom', path: 'commodityHarmonizedSystemSubHeadingCode'.split('.'), fatal: true, message: 'Invalid',
      } as ZodIssue)
    } else {
      setIsInvalidCode(false)
    }
  }, [options.length, selectedOption, setError, setValue])

  // Active (non-archived) results, mapped to react-select options.
  const selectOptions = useMemo<CommodityCodeOption[]>(
    () => options
      .filter((option) => !option.isArchived)
      .map((option) => ({
        value: option.value,
        label: `${option.codeLabel} - ${option.name}`,
        codeLabel: option.codeLabel,
        isArchived: option.isArchived,
      })),
    [options],
  )

  return (
    <Select<CommodityCodeOption, false>
      menuPlacement="auto"
      // Pure controlled search input (no selected-value "chip"): the typed /
      // committed code is always shown as the input text, mirroring the
      // TraderSearch styling. Selecting a result is handled via onChange.
      controlShouldRenderValue={false}
      className={`select menu-list--auto trader-search ${isInvalidCode ? 'form-control p-0 pe-3 is-invalid' : ''}`}
      classNamePrefix="select"
      options={selectOptions}
      // Results come pre-filtered from the server; don't filter again client-side.
      filterOption={null}
      isLoading={isLoading}
      placeholder={t('declaration.p5.commodityCodeSearch')}
      noOptionsMessage={() => t('declaration.p5.commodityCodeSearch')}
      loadingMessage={() => t('common.loading')}
      inputValue={currentSearch}
      value={null}
      onInputChange={(newValue, actionMeta) => {
        if (actionMeta.action !== 'input-change') return
        if (!DIGIT_OR_WHITESPACE.test(newValue)) return
        setCurrentSearch(newValue)
        if (newValue === '') {
          setSelectedOption(undefined)
          setOptions([])
        }
      }}
      onChange={(option) => {
        if (!option) {
          setSelectedOption(undefined)
          setCurrentSearch('')
          setOptions([])
          return
        }
        const match = options.find((item) => item.value === option.value)
        setSelectedOption(match ?? {
          value: option.value, codeLabel: option.codeLabel, name: '', isArchived: option.isArchived,
        })
        setCurrentSearch(option.codeLabel)
      }}
      onMenuScrollToBottom={() => {
        if (!isLoading && !wasPreviousPageLast) {
          setPageNumber((page) => page + 1)
        }
      }}
    />
  )
}

export default CommodityCodeSearch
